/**
 * ContentAnalyticsPage — Full analytics dashboard for article performance.
 *
 * Routes:
 *   /success/admin/content/analytics          — aggregate view (all articles)
 *   /success/admin/content/articles/:id/analytics — per-article view
 *
 * Features:
 *   - Date range picker (7d, 30d, 90d, 1y, custom)
 *   - Site selector (Success, Support, Both)
 *   - Time-series SVG line chart (views / unique sessions toggle)
 *   - Metric summary cards with period-over-period trend
 *   - Top articles sortable table (aggregate view)
 *   - Day-of-week × hour heatmap
 *   - CSV export
 *   - Auto-refresh (1m / 5m / 15m / off)
 *   - Stale data banner (X-Data-Freshness > 30 min)
 *
 * SPEC-SS-CONTENT §5.2, AC26-31
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart3, Eye, Users, Clock, TrendingUp, Share2, ThumbsUp,
  Download, RefreshCw, AlertTriangle, ChevronUp, ChevronDown,
  ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

// ─── API types ────────────────────────────────────────────────────────────────

const SS_API_BASE = '';

interface TimeseriesBucket {
  bucket: string;
  views: number;
  unique_sessions: number;
  avg_scroll_depth: number;
  avg_time_on_page_seconds: number;
  shares: number;
  helpfulness_avg: number;
  helpfulness_count: number;
}

interface AnalyticsTotals {
  views: number;
  unique_sessions: number;
  avg_scroll_depth: number;
  avg_time_on_page: number;
  shares: number;
  helpfulness_avg: number;
  helpfulness_count: number;
}

interface AnalyticsResponse {
  article_id: string;
  period: { start: string; end: string };
  granularity: string;
  site: string;
  timeseries: TimeseriesBucket[];
  totals: AnalyticsTotals;
  freshnessMinutes: number;
}

interface TopArticle {
  id: string;
  title: string;
  views: number;
  avg_time_on_page: number;
  helpfulness_avg: number;
  shares: number;
}

// ─── Date preset types ────────────────────────────────────────────────────────

type PeriodPreset = '7d' | '30d' | '90d' | '1y';
type SiteFilter = 'success' | 'support' | 'both';
type MetricKey = 'views' | 'unique_sessions';
type SortKey = 'views' | 'avg_time_on_page' | 'helpfulness_avg' | 'shares';

// ─── Utility helpers ──────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatBucket(bucket: string, granularity: string): string {
  const date = new Date(bucket);
  if (granularity === 'hourly') {
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
  }
  if (granularity === 'monthly') {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function trendArrow(current: number, prior: number): { icon: React.ReactNode; color: string } {
  if (prior === 0 || current === prior) return { icon: <Minus size={14} />, color: 'text-neutral-400' };
  if (current > prior) return { icon: <ArrowUpRight size={14} />, color: 'text-green-500' };
  return { icon: <ArrowDownRight size={14} />, color: 'text-red-500' };
}

// ─── SVG Line Chart ───────────────────────────────────────────────────────────

interface LineChartProps {
  data: TimeseriesBucket[];
  metric: MetricKey;
  granularity: string;
  height?: number;
}

function LineChart({ data, metric, granularity, height = 200 }: LineChartProps) {
  const values = data.map((d) => (metric === 'views' ? d.views : d.unique_sessions));
  const maxVal = Math.max(...values, 1);
  const width = 800;
  const padLeft = 48;
  const padRight = 16;
  const padTop = 12;
  const padBottom = 32;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-neutral-400">
        No data for selected period
      </div>
    );
  }

  const points = data.map((d, i) => {
    const x = padLeft + (i / Math.max(data.length - 1, 1)) * chartW;
    const y = padTop + (1 - (metric === 'views' ? d.views : d.unique_sessions) / maxVal) * chartH;
    return { x, y, bucket: d.bucket };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const fillD = `${pathD} L ${points[points.length - 1].x} ${padTop + chartH} L ${padLeft} ${padTop + chartH} Z`;

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    y: padTop + (1 - t) * chartH,
    label: formatNumber(Math.round(t * maxVal)),
  }));

  // X-axis labels — show at most 7 labels
  const step = Math.max(1, Math.floor(data.length / 7));
  const xLabels = data
    .filter((_, i) => i % step === 0 || i === data.length - 1)
    .map((d, idx, arr) => {
      const origIdx = data.indexOf(d);
      const x = padLeft + (origIdx / Math.max(data.length - 1, 1)) * chartW;
      return { x, label: formatBucket(d.bucket, granularity) };
    });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }}>
      {/* Grid lines */}
      {yTicks.map((tick) => (
        <line key={tick.y} x1={padLeft} y1={tick.y} x2={width - padRight} y2={tick.y}
          stroke="#e5e7eb" strokeWidth={1} strokeDasharray="4,4" />
      ))}

      {/* Y-axis labels */}
      {yTicks.map((tick) => (
        <text key={tick.y} x={padLeft - 6} y={tick.y + 4} textAnchor="end"
          fontSize={10} fill="#9ca3af">{tick.label}</text>
      ))}

      {/* Fill area */}
      <path d={fillD} fill="#6366f1" fillOpacity={0.08} />

      {/* Line */}
      <path d={pathD} fill="none" stroke="#6366f1" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

      {/* Data points */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill="#6366f1" />
      ))}

      {/* X-axis labels */}
      {xLabels.map((l, i) => (
        <text key={i} x={l.x} y={height - 6} textAnchor="middle" fontSize={9} fill="#9ca3af">{l.label}</text>
      ))}
    </svg>
  );
}

// ─── Heatmap ──────────────────────────────────────────────────────────────────

interface HeatmapProps {
  data: TimeseriesBucket[];
}

function Heatmap({ data }: HeatmapProps) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Aggregate views by day-of-week and hour
  const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  data.forEach((bucket) => {
    const d = new Date(bucket.bucket);
    const dow = d.getUTCDay();
    const hour = d.getUTCHours();
    grid[dow][hour] += bucket.views;
  });

  const maxVal = Math.max(...grid.flat(), 1);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px]">
        <div className="flex">
          <div className="w-10 shrink-0" />
          {hours.map((h) => (
            <div key={h} className="flex-1 text-center text-[9px] text-neutral-400 pb-1">
              {h % 6 === 0 ? `${h}h` : ''}
            </div>
          ))}
        </div>
        {days.map((day, dow) => (
          <div key={day} className="flex items-center mb-0.5">
            <div className="w-10 shrink-0 text-[10px] text-neutral-500 text-right pr-2">{day}</div>
            {hours.map((h) => {
              const val = grid[dow][h];
              const intensity = val / maxVal;
              const opacity = intensity > 0 ? 0.1 + intensity * 0.9 : 0;
              return (
                <div
                  key={h}
                  className="flex-1 h-5 rounded-sm mx-px"
                  style={{ backgroundColor: `rgba(99, 102, 241, ${opacity})` }}
                  title={`${day} ${h}:00 — ${val} views`}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Metric Card ──────────────────────────────────────────────────────────────

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  trend?: { icon: React.ReactNode; color: string };
  loading?: boolean;
}

function MetricCard({ icon, label, value, trend, loading }: MetricCardProps) {
  return (
    <div className="bg-white border border-neutral-100 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-neutral-400">{icon}</span>
        {trend && (
          <span className={`${trend.color} flex items-center gap-0.5 text-xs font-medium`}>
            {trend.icon}
          </span>
        )}
      </div>
      {loading ? (
        <div className="h-7 w-16 bg-neutral-100 rounded animate-pulse mb-1" />
      ) : (
        <p className="text-2xl font-semibold text-neutral-900">{value}</p>
      )}
      <p className="text-xs text-neutral-500 mt-0.5">{label}</p>
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export default function ContentAnalyticsPage() {
  const { id: articleId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isArticleView = Boolean(articleId);

  // Filter state
  const [period, setPeriod] = useState<PeriodPreset>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [site, setSite] = useState<SiteFilter>('both');
  const [metricToggle, setMetricToggle] = useState<MetricKey>('views');
  const [sortKey, setSortKey] = useState<SortKey>('views');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [autoRefresh, setAutoRefresh] = useState<number>(0); // 0=off, 60, 300, 900 seconds
  const [refreshKey, setRefreshKey] = useState(0);

  // Data state
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [topArticles, setTopArticles] = useState<TopArticle[]>([]);
  const [staleBanner, setStaleBanner] = useState<number | null>(null);

  const autoRefreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Fetch analytics ─────────────────────────────────────────────────────────

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const periodParam = useCustom && customStart && customEnd
        ? `${customStart},${customEnd}`
        : period;
      const siteParam = site !== 'both' ? `&site=${site}` : '';
      const idParam = articleId ?? 'aggregate'; // aggregate view uses a special ID

      const granularity = period === '7d' ? 'hourly' : period === '1y' ? 'monthly' : 'daily';

      const headers: Record<string, string> = {};
      const stored = localStorage.getItem('ss_access_token');
      if (stored) headers['Authorization'] = `Bearer ${stored}`;

      const url = articleId
        ? `${SS_API_BASE}/api/content/articles/${articleId}/analytics?period=${periodParam}${siteParam}&granularity=${granularity}`
        : `${SS_API_BASE}/api/content/analytics/overview?period=${periodParam}${siteParam}`;

      const res = await fetch(url, { headers });

      if (!res.ok) {
        setData(null);
        return;
      }

      // X-Data-Freshness header (AC22, AC31)
      const freshnessHeader = res.headers.get('X-Data-Freshness');
      const freshnessMinutes = freshnessHeader ? parseInt(freshnessHeader, 10) : 0;
      setStaleBanner(freshnessMinutes > 30 ? freshnessMinutes : null);

      const json = await res.json();

      if (articleId) {
        setData({ ...json, freshnessMinutes });
      } else {
        // For aggregate overview, we use the overview endpoint response
        setData(null);
        setTopArticles(Array.isArray(json.overview) ? json.overview : []);
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [articleId, period, site, useCustom, customStart, customEnd]);

  // ── Initial fetch + auto-refresh ────────────────────────────────────────────

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics, refreshKey]);

  useEffect(() => {
    if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
    if (autoRefresh > 0) {
      autoRefreshTimer.current = setInterval(() => {
        setRefreshKey((k) => k + 1);
      }, autoRefresh * 1000);
    }
    return () => {
      if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
    };
  }, [autoRefresh]);

  // ── CSV export ───────────────────────────────────────────────────────────────

  function exportCsv() {
    if (!data?.timeseries) return;
    const header = 'bucket,views,unique_sessions,avg_scroll_depth,avg_time_on_page_seconds,shares,helpfulness_avg,helpfulness_count\n';
    const rows = data.timeseries.map((b) =>
      [b.bucket, b.views, b.unique_sessions, b.avg_scroll_depth, b.avg_time_on_page_seconds, b.shares, b.helpfulness_avg, b.helpfulness_count].join(',')
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${articleId ?? 'aggregate'}-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Sort helpers ─────────────────────────────────────────────────────────────

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const sortedArticles = useMemo(() => {
    return [...topArticles].sort((a, b) => {
      const av = a[sortKey] as number;
      const bv = b[sortKey] as number;
      return sortDir === 'asc' ? av - bv : bv - av;
    }).slice(0, 20);
  }, [topArticles, sortKey, sortDir]);

  // ── Render ───────────────────────────────────────────────────────────────────

  const totals = data?.totals;

  function SortHeader({ label, field }: { label: string; field: SortKey }) {
    return (
      <button
        onClick={() => handleSort(field)}
        className="flex items-center gap-1 text-xs font-medium text-neutral-600 hover:text-neutral-900"
      >
        {label}
        {sortKey === field ? (
          sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />
        ) : (
          <ChevronDown size={12} className="text-neutral-300" />
        )}
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* ── Stale data banner (AC31) ──────────────────────────────────────── */}
      {staleBanner !== null && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center gap-2 text-sm text-amber-800">
          <AlertTriangle size={14} className="text-amber-500 shrink-0" />
          Analytics data may be up to {staleBanner} minutes old.
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 size={20} className="text-indigo-600" />
              <h1 className="text-xl font-semibold text-neutral-900">
                {isArticleView ? 'Article Analytics' : 'Content Analytics'}
              </h1>
            </div>
            <p className="text-sm text-neutral-500">
              {isArticleView
                ? 'Detailed analytics for this article'
                : 'Aggregate analytics across all published articles'}
            </p>
          </div>

          {/* Controls: refresh + export */}
          <div className="flex items-center gap-2">
            {/* Auto-refresh selector (AC30) */}
            <select
              value={autoRefresh}
              onChange={(e) => setAutoRefresh(Number(e.target.value))}
              className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white text-neutral-700"
            >
              <option value={0}>Auto-refresh: off</option>
              <option value={60}>Every 1 min</option>
              <option value={300}>Every 5 min</option>
              <option value={900}>Every 15 min</option>
            </select>

            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-600 border border-neutral-200 rounded-lg bg-white hover:bg-neutral-50"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>

            {isArticleView && data?.timeseries && (
              <button
                onClick={exportCsv}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                <Download size={13} />
                Export CSV
              </button>
            )}
          </div>
        </div>

        {/* ── Filters ────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Period presets */}
          <div className="flex items-center gap-1 bg-white border border-neutral-200 rounded-lg p-0.5">
            {(['7d', '30d', '90d', '1y'] as PeriodPreset[]).map((p) => (
              <button
                key={p}
                onClick={() => { setPeriod(p); setUseCustom(false); }}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  !useCustom && period === p
                    ? 'bg-indigo-600 text-white font-medium'
                    : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Custom date range */}
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={customStart}
              onChange={(e) => { setCustomStart(e.target.value); setUseCustom(true); }}
              className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white"
            />
            <span className="text-xs text-neutral-400">—</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => { setCustomEnd(e.target.value); setUseCustom(true); }}
              className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white"
            />
          </div>

          {/* Site selector */}
          <select
            value={site}
            onChange={(e) => setSite(e.target.value as SiteFilter)}
            className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white text-neutral-700"
          >
            <option value="both">Both sites</option>
            <option value="success">Success only</option>
            <option value="support">Support only</option>
          </select>
        </div>

        {/* ── Metric cards (AC26) ───────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <MetricCard
            icon={<Eye size={16} />}
            label="Total Views"
            value={loading ? '—' : formatNumber(totals?.views ?? 0)}
            loading={loading}
          />
          <MetricCard
            icon={<Users size={16} />}
            label="Unique Sessions"
            value={loading ? '—' : formatNumber(totals?.unique_sessions ?? 0)}
            loading={loading}
          />
          <MetricCard
            icon={<Clock size={16} />}
            label="Avg. Time on Page"
            value={loading ? '—' : formatTime(totals?.avg_time_on_page ?? 0)}
            loading={loading}
          />
          <MetricCard
            icon={<TrendingUp size={16} />}
            label="Avg. Scroll Depth"
            value={loading ? '—' : `${Math.round(totals?.avg_scroll_depth ?? 0)}%`}
            loading={loading}
          />
          <MetricCard
            icon={<ThumbsUp size={16} />}
            label="Helpfulness Score"
            value={loading ? '—' : totals?.helpfulness_count ? (totals.helpfulness_avg).toFixed(1) : '—'}
            loading={loading}
          />
          <MetricCard
            icon={<Share2 size={16} />}
            label="Shares"
            value={loading ? '—' : formatNumber(totals?.shares ?? 0)}
            loading={loading}
          />
        </div>

        {/* ── Time-series chart (AC27) ──────────────────────────────────── */}
        {isArticleView && (
          <div className="bg-white border border-neutral-100 rounded-xl p-5 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-neutral-800">Views Over Time</h2>
              {/* Toggle (AC27) */}
              <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
                <button
                  onClick={() => setMetricToggle('views')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    metricToggle === 'views'
                      ? 'bg-white text-indigo-600 font-medium shadow-sm'
                      : 'text-neutral-500'
                  }`}
                >
                  Views
                </button>
                <button
                  onClick={() => setMetricToggle('unique_sessions')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    metricToggle === 'unique_sessions'
                      ? 'bg-white text-indigo-600 font-medium shadow-sm'
                      : 'text-neutral-500'
                  }`}
                >
                  Sessions
                </button>
              </div>
            </div>
            {loading ? (
              <div className="h-48 bg-neutral-50 rounded-lg animate-pulse" />
            ) : (
              <LineChart
                data={data?.timeseries ?? []}
                metric={metricToggle}
                granularity={data?.granularity ?? 'daily'}
                height={200}
              />
            )}
          </div>
        )}

        {/* ── Top articles table (aggregate view, AC28) ─────────────────── */}
        {!isArticleView && (
          <div className="bg-white border border-neutral-100 rounded-xl shadow-sm mb-6">
            <div className="px-5 py-4 border-b border-neutral-100">
              <h2 className="text-sm font-semibold text-neutral-800">Top Articles</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 bg-neutral-50/50">
                    <th className="text-left px-5 py-3 text-xs font-medium text-neutral-600">Article</th>
                    <th className="text-right px-4 py-3">
                      <SortHeader label="Views" field="views" />
                    </th>
                    <th className="text-right px-4 py-3">
                      <SortHeader label="Avg Time" field="avg_time_on_page" />
                    </th>
                    <th className="text-right px-4 py-3">
                      <SortHeader label="Helpfulness" field="helpfulness_avg" />
                    </th>
                    <th className="text-right px-4 py-3 pr-5">
                      <SortHeader label="Shares" field="shares" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 bg-neutral-100 rounded animate-pulse w-3/4" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : sortedArticles.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-sm text-neutral-400">
                        No analytics data available for this period
                      </td>
                    </tr>
                  ) : (
                    sortedArticles.map((article) => (
                      <tr
                        key={article.id}
                        onClick={() => navigate(`${article.id}`)}
                        className="hover:bg-neutral-50 cursor-pointer"
                      >
                        <td className="px-5 py-3 font-medium text-neutral-800 truncate max-w-[300px]">
                          {article.title || article.id}
                        </td>
                        <td className="px-4 py-3 text-right text-neutral-600">{formatNumber(article.views)}</td>
                        <td className="px-4 py-3 text-right text-neutral-600">{formatTime(article.avg_time_on_page)}</td>
                        <td className="px-4 py-3 text-right text-neutral-600">
                          {article.helpfulness_avg > 0 ? article.helpfulness_avg.toFixed(1) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right pr-5 text-neutral-600">{formatNumber(article.shares)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Heatmap (day-of-week × hour) ─────────────────────────────── */}
        {isArticleView && data?.timeseries && data.timeseries.length > 0 && (
          <div className="bg-white border border-neutral-100 rounded-xl p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-neutral-800 mb-4">
              View Density by Hour &amp; Day (UTC)
            </h2>
            <Heatmap data={data.timeseries} />
          </div>
        )}
      </div>
    </div>
  );
}
